import { ReactNode, useState, useEffect, Fragment } from "react";
import { useLocation } from "wouter";
import AdminSidebar from "./AdminSidebar";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { navigationGroups } from "@/config/navigation-optimized";
import { useUser } from "@/hooks/use-user";
import { useTheme } from "@/hooks/use-theme";
import { isAdmin } from "@/lib/permissions";
import { Bell, Sun, Moon } from "lucide-react";

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [location] = useLocation();
  const { user, isLoading } = useUser();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [pageTitle, setPageTitle] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ label: string; path?: string }>>([]);

  useEffect(() => {
    const paths = location.split('/').filter(Boolean);
    const crumbs: Array<{ label: string; path?: string }> = [
      { label: 'Admin', path: '/admin' }
    ];
    let currentPageName = '';
    navigationGroups.forEach(group => {
      const item = group.items.find(item => item.path === location);
      if (item) {
        currentPageName = item.name;
        crumbs.push({ label: group.name });
        crumbs.push({ label: item.name });
      }
    });
    if (!currentPageName && paths.length > 1) {
      const lastPath = paths[paths.length - 1];
      currentPageName = lastPath.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      crumbs.push({ label: currentPageName });
    }
    setPageTitle(currentPageName || 'Dashboard');
    setBreadcrumbs(crumbs);
  }, [location]);

  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark');

  const colors = {
    bg: isDark ? '#080d19' : '#f8fafc',
    headerBg: isDark ? 'rgba(8,13,25,0.85)' : 'rgba(255,255,255,0.85)',
    headerBorder: isDark ? '#131c2e' : '#e2e8f0',
    pillBg: isDark ? '#0f1628' : '#f1f5f9',
    pillBorder: isDark ? '#1e293b' : '#e2e8f0',
    textPrimary: isDark ? '#e2e8f0' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    textMuted: isDark ? '#64748b' : '#94a3b8',
    separatorColor: isDark ? '#475569' : '#cbd5e1',
    titleColor: isDark ? '#f1f5f9' : '#0f172a',
    iconColor: isDark ? '#64748b' : '#94a3b8',
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: colors.bg }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${colors.pillBorder}`, borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (!user || !isAdmin(user.role)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: colors.bg }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#ef4444', marginBottom: 12 }}>Access Denied</h2>
          <p style={{ color: colors.textSecondary, marginBottom: 20 }}>Admin privileges are required.</p>
          <Button variant="outline" onClick={() => window.location.href = '/login'} style={{ borderColor: colors.pillBorder, color: colors.textPrimary }}>
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, display: 'flex' }}>
      <AdminSidebar />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={{
          position: 'sticky', top: 0, zIndex: 30,
          background: colors.headerBg, backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${colors.headerBorder}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, index) => (
                    <Fragment key={index}>
                      {index > 0 && <BreadcrumbSeparator style={{ color: colors.separatorColor }} />}
                      <BreadcrumbItem>
                        {crumb.path ? (
                          <BreadcrumbLink href={crumb.path} style={{ color: colors.textMuted, fontSize: 13 }}>
                            {crumb.label}
                          </BreadcrumbLink>
                        ) : index === breadcrumbs.length - 1 ? (
                          <BreadcrumbPage style={{ color: colors.textPrimary, fontSize: 13 }}>{crumb.label}</BreadcrumbPage>
                        ) : (
                          <span style={{ color: colors.separatorColor, fontSize: 13 }}>{crumb.label}</span>
                        )}
                      </BreadcrumbItem>
                    </Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 12px', borderRadius: 8,
                background: colors.pillBg, border: `1px solid ${colors.pillBorder}`,
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
                <span style={{ fontSize: 12, color: colors.textSecondary }}>System Healthy</span>
              </div>
              <button
                onClick={toggleTheme}
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: colors.pillBg, border: `1px solid ${colors.pillBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {isDark ? (
                  <Sun style={{ width: 16, height: 16, color: '#f59e0b' }} />
                ) : (
                  <Moon style={{ width: 16, height: 16, color: '#6366f1' }} />
                )}
              </button>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: colors.pillBg, border: `1px solid ${colors.pillBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}>
                <Bell style={{ width: 16, height: 16, color: colors.iconColor }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 600, color: '#fff',
                }}>
                  {user?.username?.charAt(0)?.toUpperCase() || 'A'}
                </div>
                <div style={{ display: 'none' }} className="sm:block">
                  <p style={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, margin: 0, lineHeight: 1.2 }}>{user?.username}</p>
                  <p style={{ fontSize: 11, color: colors.textMuted, margin: 0, textTransform: 'capitalize' }}>{user?.role}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 24px 48px' }}>
            {pageTitle && (
              <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 26, fontWeight: 700, color: colors.titleColor, letterSpacing: '-0.02em' }}>{pageTitle}</h1>
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
